
import React from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UseFormReturn } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AccountTypeBadge } from '@/components/common/AccountTypeBadge';

interface TradingAccount {
  id: string;
  number_account_trading: string;
  live: boolean;
  balance_account: number;
  api_connection_id?: string;
  isDisconnected?: boolean;
}

interface TradingAccountFieldProps {
  form: UseFormReturn<any>;
  isLoading: boolean;
  accounts: TradingAccount[];
  hasCspProfile: boolean;
  disabled?: boolean;
}

export const TradingAccountField: React.FC<TradingAccountFieldProps> = ({
  form,
  isLoading,
  accounts,
  hasCspProfile,
  disabled = false
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name="trading_account_id"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('trading_account')}</FormLabel>
          <Select
            onValueChange={field.onChange}
            value={field.value}
            disabled={!hasCspProfile || isLoading || disabled}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={t('select_trading_account')} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {isLoading ? (
                <SelectItem value="loading" disabled>{t('loading_accounts')}</SelectItem>
              ) : accounts.length > 0 ? (
                accounts.map(account => (
                  <SelectItem 
                    key={account.id} 
                    value={account.id} 
                    className={`py-3 ${account.isDisconnected ? "text-muted-foreground" : ""}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2 mr-4">
                        {account.isDisconnected && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <AlertTriangle className="h-4 w-4 text-amber-500 mr-1" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('Account Disconnected')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <span className="font-medium">{account.number_account_trading}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <AccountTypeBadge isLive={account.live} />
                        {account.isDisconnected && (
                          <Badge variant="outline" className="border-amber-500 text-amber-500">
                            {t('disconnected')}
                          </Badge>
                        )}
                        <span className="text-sm font-medium">
                          ${(account.balance_account || 0).toLocaleString('en-US', { 
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2 
                          })}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="none" disabled>
                  {hasCspProfile 
                    ? t('no_available_trading_accounts')
                    : t('select_ctrader_profile_first')}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
